import { GetStaticPropsResult, GetStaticPathsResult } from 'next'
import { ParsedUrlQuery } from 'querystring'
import { useContext } from 'react'

import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import {
  SanityProductCatalogueQuery,
  SanityPrintProductPage,
  SanityProductFragment,
} from '@data/sanity/queries/types/product'
import { getPreview } from '@lib/sanity/client'
import { getPrintProductPage, getProductCatalogue } from '@lib/sanity/product'
import { Locale } from '@lib/language'
import { getProductGalleryWithPrintAreas } from '@lib/print/image'
import { useProductWithInventory } from '@lib/product'
import {
  ProductVariantSelectionContext,
  ProductVariantSelectionContextProvider,
} from '@lib/product-variant-selection-context'
import { useSetPrintShop } from '@lib/print/shop'
import {
  CustomStaticPropsContext,
  getPageUrl,
  PageType,
  PageUrlOptions,
} from '@lib/routes'

import Layout from '@components/layout'
import PrintConfigurator from '@modules/shop/print-configurator'

interface ShopPrintConfiguratorPageContentProps {
  product: SanityProductFragment
  page: SanityPrintProductPage
  site: SanitySiteFragment
}

interface ShopPrintConfiguratorPageProps {
  locale: Locale
  page: SanityPrintProductPage
  site: SanitySiteFragment
  productCatalogue: SanityProductCatalogueQuery
}

type StaticShopPrintConfiguratorPageParams = ParsedUrlQuery & {
  slug: string
  id: string
}

const ShopPrintConfiguratorPageContent = ({
  product,
  page,
  site,
}: ShopPrintConfiguratorPageContentProps) => {
  const {
    printShop,
    printPermissionSettings,
    printConfiguratorStrings,
    printFeeSettings,
  } = page

  const { selectedVariant } = useContext(ProductVariantSelectionContext)

  useSetPrintShop(printShop)

  if (
    !selectedVariant ||
    !printShop ||
    !printPermissionSettings?.printPermissions ||
    !product.printConfiguration ||
    !printFeeSettings
  ) {
    return null
  }

  const productGalleryWithPrintAreas = getProductGalleryWithPrintAreas(
    product.photos.main,
    product.printConfiguration,
  )

  // Get product URL
  const urlOptions: PageUrlOptions = {
    parameters: {
      variant:
        product.surfaceOption && selectedVariant ? `${selectedVariant.id}` : '',
    },
  }
  const productUrl = getPageUrl(
    PageType.SHOP_PRODUCT,
    [printShop.slug, product.slug],
    urlOptions,
  )

  return (
    <Layout page={page} site={site}>
      <PrintConfigurator
        shopId={printShop.id}
        institution={printShop.institution}
        prints={printShop.prints ?? []}
        productType={product.type}
        printConfiguration={product.printConfiguration}
        printPermissions={printPermissionSettings.printPermissions}
        productGalleryWithPrintAreas={productGalleryWithPrintAreas}
        productVariant={selectedVariant}
        productUrl={productUrl}
        printConfiguratorStrings={printConfiguratorStrings}
        printFeeCombinations={printFeeSettings.printFeeCombinations}
      />
    </Layout>
  )
}

/**
 * The product print page.
 */
const ShopPrintConfiguratorPage = ({
  locale,
  page,
  site,
}: ShopPrintConfiguratorPageProps) => {
  const product = useProductWithInventory(page, locale)

  return (
    <ProductVariantSelectionContextProvider
      product={product}
      useFallbackVariant
    >
      <ShopPrintConfiguratorPageContent
        product={product}
        page={page}
        site={site}
      />
    </ProductVariantSelectionContextProvider>
  )
}

export async function getStaticProps({
  locale,
  params,
  preview,
  previewData,
}: CustomStaticPropsContext<StaticShopPrintConfiguratorPageParams>): Promise<
  GetStaticPropsResult<ShopPrintConfiguratorPageProps>
> {
  const printShopSlug = params?.id ?? ''
  const productSlug = params?.slug ?? ''
  const productPage = await getPrintProductPage(
    locale,
    printShopSlug,
    productSlug,
    getPreview(preview, previewData),
  )

  if (!productPage.page || !productPage.page.printShop) {
    return {
      notFound: true,
    }
  }

  const productCatalogue = await getProductCatalogue(locale)

  return {
    props: {
      locale,
      page: productPage.page,
      site: productPage.site,
      productCatalogue,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticShopPrintConfiguratorPageParams>
> {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export default ShopPrintConfiguratorPage
